import { useState } from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { PageHeader } from '@/components/shared/PageHeader';
import { Breadcrumbs } from '@/components/shared/Breadcrumbs';
import { LoadingState } from '@/components/shared/LoadingState';
import { ErrorState } from '@/components/shared/ErrorState';
import { DataTablePagination } from '@/components/shared/DataTablePagination';
import { CopyButton } from '@/components/shared/CopyButton';
import { AddressDisplay } from '@/components/shared/AddressDisplay';
import { AmountDisplay } from '@/components/shared/AmountDisplay';
import { DateDisplay } from '@/components/shared/DateDisplay';
import { StatusBadge } from '@/components/shared/StatusBadge';
import { TransactionTable } from '@/components/features/transactions/TransactionTable';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { useClientStaticGroup, useClientStaticGroupTransactions } from '@/lib/hooks';
import { ROUTES } from '@/config/routes';
import type { ClientStaticWalletStatus, StatusBadgeConfig } from '@/lib/types';

export const Route = createFileRoute('/_auth/_admin/admin/wallets/client-static/$id')({
  component: ClientStaticWalletDetailComponent,
});

const STATUS_CONFIG: Record<ClientStaticWalletStatus, StatusBadgeConfig> = {
  active: { label: 'Активен', variant: 'secondary', color: 'text-green-600' },
  banned: { label: 'Заблокирован', variant: 'destructive', color: 'text-red-600' },
  retired: { label: 'Заменён', variant: 'outline', color: 'text-muted-foreground' },
};

function ClientStaticWalletDetailComponent() {
  const { id } = Route.useParams();
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(20);

  const { data: group, isLoading, isError, error } = useClientStaticGroup(Number(id));
  const { data: txData, isLoading: txLoading } = useClientStaticGroupTransactions(Number(id), {
    page,
    per_page: perPage,
  });

  const breadcrumbs = [
    { label: 'Кошельки', href: ROUTES.ADMIN.WALLETS },
    { label: 'Кошельки клиентов', href: ROUTES.ADMIN.CLIENT_STATIC_WALLETS },
    { label: group?.client_id ?? `#${id}` },
  ];

  if (isLoading) {
    return <LoadingState />;
  }

  if (isError) {
    return <ErrorState title={error.name} message={error.message} />;
  }

  if (!group) {
    return <ErrorState title="Не найдено" message="Кошельки клиента не найдены" />;
  }

  return (
    <div className="space-y-6">
      <Breadcrumbs items={breadcrumbs} />

      <PageHeader
        title={group.merchant_id ? `Клиент ${group.merchant_id} / ${group.client_id}` : `Клиент ${group.client_id}`}
        description={group.owner ? `${group.owner.name} · ${group.owner.email}` : undefined}
      />

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">client_id</CardTitle>
          </CardHeader>
          <CardContent className="flex items-center gap-2">
            <code className="rounded bg-muted px-2 py-1 text-sm font-mono">{group.client_id}</code>
            <CopyButton text={group.client_id} />
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">merchant_id</CardTitle>
          </CardHeader>
          <CardContent className="flex items-center gap-2">
            {group.merchant_id ? (
              <>
                <code className="rounded bg-muted px-2 py-1 text-sm font-mono">{group.merchant_id}</code>
                <CopyButton text={group.merchant_id} />
              </>
            ) : (
              <span className="text-muted-foreground">— (без merchant_id)</span>
            )}
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Кошельков</CardTitle>
          </CardHeader>
          <CardContent className="text-2xl font-semibold">
            {group.summary.wallet_count}
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Суммарный баланс
            </CardTitle>
          </CardHeader>
          <CardContent className="text-2xl font-semibold">
            <AmountDisplay amount={group.summary.total_balance} />
          </CardContent>
        </Card>
      </div>

      <div>
        <h2 className="mb-3 text-lg font-semibold">Кошельки</h2>
        <div className="rounded-md border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>ID</TableHead>
                <TableHead>Адрес</TableHead>
                <TableHead>Баланс</TableHead>
                <TableHead>Статус</TableHead>
                <TableHead>Последнее использование</TableHead>
                <TableHead>Создан</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {group.wallets.map((wallet) => (
                <TableRow key={wallet.id}>
                  <TableCell className="font-medium">#{wallet.id}</TableCell>
                  <TableCell>
                    <AddressDisplay address={wallet.address} />
                  </TableCell>
                  <TableCell>
                    <AmountDisplay amount={wallet.balance} />
                  </TableCell>
                  <TableCell>
                    <StatusBadge status={wallet.status} config={STATUS_CONFIG} />
                  </TableCell>
                  <TableCell>
                    {wallet.last_used_at ? (
                      <DateDisplay date={wallet.last_used_at} format="full" />
                    ) : (
                      <span className="text-muted-foreground">—</span>
                    )}
                  </TableCell>
                  <TableCell>
                    <DateDisplay date={wallet.created_at} format="short" />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </div>

      <div>
        <h2 className="mb-3 text-lg font-semibold">Транзакции</h2>
        <TransactionTable
          data={txData?.data ?? []}
          isLoading={txLoading}
          onRowClick={(tx) => navigate({ to: ROUTES.ADMIN.TRANSACTION_DETAIL(tx.id) })}
        />
        {txData?.meta && (
          <div className="mt-4">
            <DataTablePagination
              meta={txData.meta}
              onPageChange={setPage}
              onPerPageChange={(value) => {
                setPerPage(value);
                setPage(1);
              }}
            />
          </div>
        )}
      </div>
    </div>
  );
}
